"use client";
import React from "react";
import "bulma/css/bulma.css";
import "bulma-extensions/bulma-tooltip/dist/css/bulma-tooltip.min.css";
import "react-datepicker/dist/react-datepicker.css";
import "@/assets/css/theme.css";
import "react-toastify/dist/ReactToastify.min.css";
import { apolloClient } from "@/graphql/apollo-client";
import { LineSignupSessionContextProvider } from "@/utils/line-signup-session-context";
import { ApolloProvider } from "@apollo/client";
import { I18nextProvider } from "react-i18next";
import { i18n } from "@/utils/localize-utils";
import ErrorBoundary from "@/component/error-boundary";
import { ToastContainer } from "react-toastify";
import { useRouter } from "next/navigation";
import { useEffect } from "react";
import { AccountsEnvLiteral, convertEnv } from "@/utils/env-state";
import { replaceFavicon } from "@graffer-inc/graffer-design-kit";
import * as Sentry from "@sentry/browser";
import { ScrollToTop } from "@/utils/scroll-to-top";

export default function App({ children }: { children: React.ReactNode }) {
  const router = useRouter();

  useEffect(() => {
    if (process.env.NEXT_PUBLIC_MAINTENANCE_MODE === "yes") {
      router.push("/maintenance");
    }
  }, [router]);

  // Sentryの初期化
  useEffect(() => {
    document.addEventListener(
      "securitypolicyviolation",
      (evt: SecurityPolicyViolationEvent) => {
        const { target } = evt;
        const extraData = {
          event: evt,
          blockedURI: evt.blockedURI,
          violatedDirective: evt.violatedDirective,
          originalPolicy: evt.originalPolicy,
          activeElement: "",
        };

        if (target instanceof Document && target.activeElement) {
          extraData.activeElement = target.activeElement.outerHTML;
        }

        Sentry.captureException(new Error(evt.type), {
          extra: extraData,
        });
      },
    );
    return () => {
      document.removeEventListener(
        "securitypolicyviolation",
        (evt: SecurityPolicyViolationEvent) => {
          const { target } = evt;
          const extraData = {
            event: evt,
            blockedURI: evt.blockedURI,
            violatedDirective: evt.violatedDirective,
            originalPolicy: evt.originalPolicy,
            activeElement: "",
          };

          if (target instanceof Document && target.activeElement) {
            extraData.activeElement = target.activeElement.outerHTML;
          }

          Sentry.captureException(new Error(evt.type), {
            extra: extraData,
          });
        },
      );
    };
  }, []);

  useEffect(() => {
    if (
      document &&
      document.querySelector<HTMLLinkElement>('link[rel="icon"]')
    ) {
      const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
      if (link && process.env.NEXT_PUBLIC_ENVIRONMENT) {
        replaceFavicon(
          link,
          convertEnv(
            process.env.NEXT_PUBLIC_ENVIRONMENT! as AccountsEnvLiteral,
          ),
        );
      }
    }
    // 環境ごとにfaviconを差し替えて区別しやすくする
  }, []);

  return (
    <LineSignupSessionContextProvider>
      <ApolloProvider client={apolloClient}>
        <I18nextProvider i18n={i18n.i18next}>
          <ErrorBoundary>
            <ToastContainer hideProgressBar={true} autoClose={false} />
            <ScrollToTop />
            {children}
          </ErrorBoundary>
        </I18nextProvider>
      </ApolloProvider>
    </LineSignupSessionContextProvider>
  );
}
